/**
 * Purchase-order totals (z8nrz7f7dz), for the live footer while a PO is edited.
 *
 * Mirrors cgt-backend src/inventory/purchase-orders/po-totals.ts
 * (computePoTotals), which is what the saved order carries
 * — keep the two in sync, or the footer will show a different total from the
 * one the order is saved with. Pure and dependency-free, so it is tested here.
 *
 *   line total = qty × unit price × (1 − discount % / 100)
 *   tax        = Σ line total × (line tax % ?? order tax %), each line to 2 dp
 *   grand      = subtotal + shipping + tax
 */

export interface PoTotalsLine {
  qty: number
  unitPrice: number
  discountPct?: number | null
  /** Null or absent: the order's rate applies. */
  taxPct?: number | null
}

export interface PoTotals {
  lineTotals: number[]
  subtotal: number
  discountTotal: number
  tax: number
  shipping: number
  grandTotal: number
}

/** Half away from zero at the cent, by decimal shifting. Identical to the backend's (po-totals.ts). */
export function round2(n: number): number {
  if (!Number.isFinite(n)) return n
  // Shift the decimal point in the number's text, not by multiplying, so no
  // binary fraction creeps in; round half away from zero; never return -0.
  const shift = (x: number, by: number) => {
    const [mantissa, exp] = String(x).split('e')
    return Number(`${mantissa}e${(exp ? Number(exp) : 0) + by}`)
  }
  const r = Math.sign(n) * shift(Math.round(shift(Math.abs(n), 2)), -2)
  return r === 0 ? 0 : r
}

export function computePoTotals(
  lines: PoTotalsLine[],
  order: { taxRatePct?: number | null; shippingCost?: number | null } = {},
): PoTotals {
  const orderRate = order.taxRatePct ?? 0
  let subtotal = 0
  let gross = 0
  let tax = 0
  const lineTotals = lines.map((l) => {
    const lineGross = l.qty * l.unitPrice
    const total = round2(lineGross * (1 - (l.discountPct ?? 0) / 100))
    gross += round2(lineGross)
    subtotal += total
    tax += round2((total * (l.taxPct ?? orderRate)) / 100)
    return total
  })
  const shipping = round2(order.shippingCost ?? 0)
  subtotal = round2(subtotal)
  tax = round2(tax)
  return {
    lineTotals,
    subtotal,
    // Gross less what the lines came to, as the backend reports it.
    discountTotal: round2(gross - subtotal),
    tax,
    shipping,
    grandTotal: round2(subtotal + shipping + tax),
  }
}

/** A money figure with its currency, e.g. "EUR 1,234.50". */
export function formatMoney(n: number | null | undefined, currency?: string | null): string {
  if (n == null) return '—'
  const figure = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${currency} ${figure}` : figure
}
